import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Grid,
  TextField,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Button,
  Typography,
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  Paper,
} from "@mui/material";
import dayjs from "dayjs";
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigate, useLocation } from "react-router-dom";
import {
  updatepos,
  ApproveSolistapi,
  DivisionList,
  GetApprovedSuppliers as fetchSuppliers,
  ApproveIndentlistapi as IndentListApi,
  postpos,
  GetNextPoNo,
} from "../../../Services/Services";
import Autocomplete from "@mui/material/Autocomplete";
import { toast, ToastContainer } from "react-toastify";
import AddItemForm from "../Inventory/ItemForm";
import { debounce } from "lodash";

export default function PurchaseOrderForm() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const [suppliers, setSuppliers] = useState([]);
  const [supplierSearch, setSupplierSearch] = useState("");
  const [indents, setIndents] = useState([]);
  const [indentLoading, setIndentLoading] = useState(false);
  const [indentSearch, setIndentSearch] = useState("");
  const [projects, setProjects] = useState([]);
  const location = useLocation();

  const { poData, isEdit } = location.state || {};
  const getSelectedDivision = () => {
    try {
      const stored = localStorage.getItem("selectedDivision");
      return stored ? JSON.parse(stored) : null;
    } catch (e) {
      console.error("Failed to parse selectedDivision:", e);
      return null;
    }
  };

  const storedDivision = getSelectedDivision();
  // Default "Billed To" data
  const [billedTo, setBilledTo] = useState({
    name: "CRANOIST MHE PVT. Ltd.",

    state_code: "22",

    gst_no: storedDivision?.gst_no,
    pan_no: storedDivision?.pan_no,
    address: storedDivision?.address,
    contact: "9825586999",
    email: "info@cranoistmhe.com",
  });

  const [formData, setFormData] = useState({
    po_number: "",
    indent: "",
    supplier: "",
    division: storedDivision?.division_id || "",
    division_name: storedDivision?.division || "",
    project: "",
    billing_name: "",
    billing_address: "",
    billing_pan: "",
    billing_gst: "",
    billing_division: storedDivision?.division || "",
    billing_email: "",
    billing_contact: "",
    shipping_address: "",
    shipping_contact: "",
    shipping_phone: "",
    items: [
      {
        category: "",
        category_name: "",
        sub_category: "",
        sub_category_name: "",
        item: "",
        description: "",
        indent_qty: 0,
        quantity: 0,
        used_qty: 0,
        unit: "",
        unit_name: "",
        size: "",
        size_name: "",
        rate: "",
        amount: "",
      },
    ],
    created_by: localStorage.getItem("user_id"),
  });

  const navigate = useNavigate();
  const [openItemModal, setOpenItemModal] = useState(false);
  const [includeShipping, setIncludeShipping] = useState(false);

  const [terms, setTerms] = useState([
    { key: "Delivery Period", value: "Ready stock", is_default: true },
    { key: "Warranty", value: "12 months from delivery", is_default: true },
    { key: "Freight", value: "Ex works. Raipur", is_default: true },
    { key: "Inspection", value: "By buyer at site", is_default: true },
    { key: "Quality", value: "As per specifications", is_default: true },
    {
      key: "Rejection/Claim",
      value: "Allowed within 7 days",
      is_default: true,
    },
    { key: "Jurisdiction", value: "Durg", is_default: true },
    {
      key: "Payment Terms",
      value: "Material received after 30 days",
      is_default: true,
    },
    // { key: "GST", value: "18% applicable", is_default: true },
    // { key: "Billing gst no-", value: storedDivision?.gst_no, is_default: true },
  ]);

  const totalValue = [...formData.items].reduce(
    (sum, i) => sum + (parseFloat(i.rate) || 0) * (parseFloat(i.quantity) || 0),
    0,
  );

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...formData.items];

    if (field === "quantity") {
      const enteredQty = parseFloat(value || 0);
      const availableQty = parseFloat(updatedItems[index].main_indent_qty) || 0;
      const indentQty = parseFloat(updatedItems[index].indent_qty) || 0;
      const usedQty = parseFloat(updatedItems[index].used_qty) || 0;

      // Check against available quantity
      if (availableQty > 0 && enteredQty > availableQty) {
        toast.error(
          `Cannot exceed available quantity!\n` + `Available: ${availableQty}`,
        );
        return; // ✅ Exit before setting value
      }
      if (enteredQty < 0) {
        toast.error("Quantity cannot be negative");
        return;
      }
    }

    updatedItems[index][field] = value;

    // Calculate amount
    const qty = parseFloat(updatedItems[index].quantity) || 0;
    const rate = parseFloat(updatedItems[index].rate) || 0;
    updatedItems[index].amount = (qty * rate).toFixed(2);

    setFormData((prev) => ({
      ...prev,
      items: updatedItems,
    }));
  };

  const debouncedFetchIndents = useCallback(
    debounce(async (searchText) => {
      const divisionId = storedDivision?.division_id;

      if (!divisionId) {
        setIndents([]);
        toast.warning("Please select a division first");
        return;
      }

      try {
        setIndentLoading(true);
        const filters = {
          division: divisionId,
          ...(searchText && { indent_no: searchText }),
        };

        const response = await IndentListApi(1, filters);
        const results = Array.isArray(response.results)
          ? response.results
          : response || [];
        setIndents(results);
      } catch (err) {
        toast.error("Failed to load indents");
        setIndents([]);
      } finally {
        setIndentLoading(false);
      }
    }, 500),
    [],
  );

  const fetchIndentsByDivision = async (divisionId) => {
    const finalDivisionId = divisionId || getSelectedDivision()?.division_id;

    if (!finalDivisionId) {
      setIndents([]);
      return;
    }

    try {
      setIndentLoading(true);
      const response = await IndentListApi(1, { division: finalDivisionId });
      const results = Array.isArray(response.results)
        ? response.results
        : response || [];
      setIndents(results);
    } catch (err) {
      toast.error("Failed to load indents");
      setIndents([]);
    } finally {
      setIndentLoading(false);
    }
  };

  const handleIndentSearchChange = (searchText) => {
    setIndentSearch(searchText);
    debouncedFetchIndents(searchText);
  };

  const fetchAllProjects = async () => {
    const division = storedDivision?.division_id;
    try {
      const response = await ApproveSolistapi(1, { division: division });
      const results = Array.isArray(response.results)
        ? response.results
        : response || [];
      setProjects(results);
    } catch (err) {
      toast.error("Failed to load projects");
    }
  };

  const debouncedFetchSuppliers = useCallback(
    debounce(async (search) => {
      try {
        const response = await fetchSuppliers(1, { name: search });
        const results = Array.isArray(response.results)
          ? response.results
          : response || [];
        setSuppliers(results);
      } catch (err) {
        toast.error("Failed to load suppliers");
      }
    }, 500),
    [],
  );

  useEffect(() => {
    debouncedFetchSuppliers("");
  }, []);

  useEffect(() => {
    const fetchDropdowns = async () => {
      try {
        fetchAllProjects();
      } catch (error) {
        console.error("Error fetching dropdowns:", error);
      }
    };

    const divisionData = getSelectedDivision();

     if (!divisionData?.division_id && !isEdit) {
    toast.warning("Please select a division from the sidebar");
    setIndents([]);
    setProjects([]);
    return;
  }

    if (isEdit && poData) {
      setFormData({
        ...formData,
        ...poData,
        created_by: localStorage.getItem("user_id"),
        billing_email: poData.billing_email || "",
        billing_contact: poData.billing_contact || "",
        division: divisionData?.division_id || poData.division || "",
        division_name: divisionData?.division || poData.division_name || "",
        items: (poData.items || []).map((item) => ({
          ...item,
          rate: parseFloat(item.rate) || 0,
          quantity: parseFloat(item.quantity) || 0,
          amount: (
            (parseFloat(item.rate) || 0) * (parseFloat(item.quantity) || 0)
          ).toFixed(2),
          size: item.size || "",
          size_name: item.size_name || item.size?.name || "",
        })),
      });
      setIncludeShipping(!!poData.shipping_address);
      setTerms(poData.terms_conditions || []);

      if (divisionData?.division_id) {
        fetchIndentsByDivision(divisionData.division_id);
      }

      if (poData.billed_to) {
        setBilledTo(poData.billed_to);
      }
    } else {
      if (divisionData?.division_id) {
        fetchIndentsByDivision(divisionData.division_id);
      }
    }

    fetchDropdowns();
    if (!isEdit && !poData?.po_id) {
      fetchNextPoNo();
    }
  }, []);

  useEffect(() => {
    const handleStorageChange = () => {
      const stored = localStorage.getItem("selectedDivision");
      if (stored) {
        try {
          const divisionData = JSON.parse(stored);
          setBilledTo((prev) => ({
            ...prev,
            gst_no: divisionData?.gst_no || prev.gst_no,
            pan_no: divisionData?.pan_no || prev.pan_no,
            address: divisionData?.address || prev.address,
          }));

          // Update terms GST
          setTerms((prev) =>
            prev.map((term) =>
              term.key === "Billing GST No"
                ? { ...term, value: divisionData?.gst_no || "" }
                : term,
            ),
          );
        } catch (e) {
          console.error("Failed to parse selectedDivision:", e);
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  const fetchNextPoNo = async () => {
    const data = await GetNextPoNo();
    if (data && data.po_number) {
      setFormData((prev) => ({
        ...prev,
        po_number: data.po_number,
      }));
    }
  };

  const handleChange = (field) => (e) => {
    setFormData({ ...formData, [field]: e.target.value });
  };

  const handleBilledToChange = (field) => (e) => {
    setBilledTo({ ...billedTo, [field]: e.target.value });
  };

  const handleTermChange = (index, field, value) => {
    const updatedTerms = [...terms];
    updatedTerms[index][field] = value;
    setTerms(updatedTerms);
  };

  const [errors, setErrors] = useState({
    billing_pan: "",
    billing_gst: "",
  });

  const validateField = (field, value) => {
    let error = "";

    if (field === "billing_pan") {
      const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
      if (value && !panRegex.test(value.toUpperCase())) {
        error = "Invalid PAN format (eg. AAAPZ1234C)";
      }
    }

    if (field === "billing_gst") {
      const gstRegex =
        /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
      if (value && !gstRegex.test(value.toUpperCase())) {
        error = "Invalid GSTIN format (eg. 27ABCDE1234F1Z5)";
      }
    }

    setErrors((prev) => ({ ...prev, [field]: error }));
    return error;
  };

  const handleCloseItemModal = async () => {
    setOpenItemModal(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const divisionData = getSelectedDivision();

    if (!divisionData?.division_id) {
      toast.error("No division selected. Please select a division first.");
      return;
    }

    const newErrors = {};

    if (!formData.po_number) newErrors.po_number = "PO Number is required";
    if (!formData.supplier) newErrors.supplier = "Supplier is required";
    if (!formData.project) newErrors.project = "Project is required";
    if (!formData.indent) newErrors.indent = "Indent is required";
    if (!formData.billing_name)
      newErrors.billing_name = "Supplier Name is required";
    if (!formData.billing_address)
      newErrors.billing_address = "Supplier Address is required";

    formData.items.forEach((item, index) => {
      if (!item.quantity || parseFloat(item.quantity) <= 0) {
        newErrors[`items.${index}.quantity`] = "Valid quantity is required";
      }
      if (!item.rate || parseFloat(item.rate) < 0) {
        newErrors[`items.${index}.rate`] = "Valid rate is required";
      }
    });

    const panError = validateField("billing_pan", formData.billing_pan);
    if (panError) newErrors.billing_pan = panError;

    const gstError = validateField("billing_gst", formData.billing_gst);
    if (gstError) newErrors.billing_gst = gstError;

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error("Please fix all validation errors before submitting");

      setTimeout(() => {
        const firstErrorElement =
          document.querySelector(".Mui-error") ||
          document.querySelector('input[style*="border: 1px solid red"]');
        if (firstErrorElement) {
          firstErrorElement.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }, 100);

      return;
    }

    setErrors({});

    const filteredTerms = terms.filter(
      (term) => term.key?.trim() !== "" && term.value?.trim() !== "",
    );

    const cleanedItems = formData.items.map((item) => ({
      ...item,
      category: item.category || null,
      category_name: item.category_name || "",
      sub_category: item.sub_category || null,
      sub_category_name: item.sub_category_name || "",
      item: item.item || null,
      unit_name: item.unit_name || "",
      size: item.size || null,
      size_name: item.size_name || "",
      quantity: parseFloat(item.quantity) || 0,
      rate: parseFloat(item.rate) || 0,
      amount: parseFloat(item.amount) || 0,
      indent_qty: parseFloat(item.indent_qty) || 0,
      used_qty: parseFloat(item.used_qty) || 0,
    }));

    const payload = {
      indent: formData.indent,
      supplier: formData.supplier,
      project: formData.project,
      division: divisionData.division_id,
      billing_name: formData.billing_name,
      billing_address: formData.billing_address,
      billing_pan: formData.billing_pan,
      billing_gst: formData.billing_gst,
      billing_division: divisionData.division || "",
      billing_email: formData.billing_email,
      billing_contact: formData.billing_contact,

      billed_to_name: billedTo.name || "",
      billed_to_address: billedTo.address || "",
      billed_to_state_name: billedTo.state_name || "",
      billed_to_state_code: billedTo.state_code || "",
      billed_to_gst_no: billedTo.gst_no || "",
      billed_to_pan_no: billedTo.pan_no || "",
      billed_to_contact: billedTo.contact || "",
      billed_to_email: billedTo.email || "",

      shipping_address: formData.shipping_address,
      shipping_contact: formData.shipping_contact,
      shipping_phone: formData.shipping_phone,
      items: cleanedItems,
      include_shipping: includeShipping,
      terms_conditions: filteredTerms,
      created_by: localStorage.getItem("user_id"),
      quotation_date: formData.quotation_date
        ? dayjs(formData.quotation_date).format("YYYY-MM-DD")
        : null,
    };

    if (!isEdit && !poData?.po_id) {
      payload.po_number = formData.po_number;
    }

    console.log("Payload being sent:", JSON.stringify(payload, null, 2));

    try {
      if (isEdit && poData?.po_id) {
        // ✅ For edit, use PATCH/PUT without po_number
        await updatepos(poData.po_id, payload);
        toast.success("Purchase Order updated successfully");
      } else {
        // ✅ For create, include po_number
        await postpos(payload);
        toast.success("Purchase Order created successfully");
      }

      sessionStorage.removeItem("poDraft");
      sessionStorage.removeItem("poTerms");
      sessionStorage.removeItem("billedTo");

      setTimeout(() => {
        navigate("/dashboard/po-list");
      }, 1500);

      if (!isEdit) {
        setFormData({
          po_number: "",
          indent: "",
          supplier: "",
          division: "",
          project: "",
          billing_name: "",
          billing_address: "",
          billing_pan: "",
          billing_gst: "",
          billing_division: "",
          shipping_address: "",
          shipping_contact: "",
          shipping_phone: "",
          billing_email: "",
          billing_contact: "",
          items: [createEmptyItem()],
          created_by: localStorage.getItem("user_id"),
        });
        setTerms([]);
        setIncludeShipping(false);
        setBilledTo({
          name: "CRANOIST MHE Pvt. Ltd.",
          address: storedDivision?.address,
          state_name: "Chhattisgarh",
          state_code: "22",
          gst_no: storedDivision?.gst_no,
          pan_no: storedDivision?.pan_no,
          contact: "+91 9825586999 ",
          email: "info@cranoistmhe.com",
        });
      }
    } catch (err) {
      console.error("Submit error:", err);

      // ✅ Better error handling
      if (err.response?.data) {
        const errorData = err.response.data;

        // Handle specific field errors
        if (errorData.po_number) {
          toast.error(`PO Number Error: ${errorData.po_number}`);
        } else if (errorData.detail) {
          toast.error(errorData.detail);
        } else if (errorData.error) {
          toast.error(errorData.error);
        } else {
          toast.error("Failed to save Purchase Order");
        }
      } else {
        toast.error("Failed to save Purchase Order");
      }
    }
  };

  const removeTerm = (index) => {
    const updatedTerms = terms.filter((_, i) => i !== index);
    setTerms(updatedTerms);
  };

  const inputStyle = {
    width: "100%",
    padding: "8px",
    fontSize: "0.875rem",
    backgroundColor: "#ffffff",
    border: "1px solid #c4c4c4",
    borderRadius: "4px",
    boxSizing: "border-box",
  };

  const handleSaveDraft = () => {
    sessionStorage.setItem("poDraft", JSON.stringify(formData));
    sessionStorage.setItem("poTerms", JSON.stringify(terms));
    sessionStorage.setItem("billedTo", JSON.stringify(billedTo));
    toast.info("Purchase Order draft saved!");
  };

  useEffect(() => {
    const draftForm = sessionStorage.getItem("poDraft");
    const draftTerms = sessionStorage.getItem("poTerms");
    const draftBilledTo = sessionStorage.getItem("billedTo");
    if (draftForm) {
      try {
        setFormData(JSON.parse(draftForm));
        if (draftTerms) setTerms(JSON.parse(draftTerms));
        if (draftBilledTo) setBilledTo(JSON.parse(draftBilledTo));
        toast.info("Loaded Purchase Order draft");
      } catch (e) {
        console.error("Error loading draft:", e);
      }
    }
  }, []);

  const handleIndentChange = (indentId) => {
    const selectedIndent = indents.find(
      (ind) => String(ind.indent_id) === String(indentId),
    );

    if (selectedIndent) {
      const selectedProject = projects.find(
        (proj) => String(proj.id) === String(selectedIndent.project),
      );

      const processedItems = (selectedIndent.items || []).map((item) => {
        const sizeId = item.size_id || item.size?.id || item.size || "";
        const sizeName = item.size_name || item.size?.name || item.size || "";

        const indentQty = parseFloat(item.indent_qty) || 0;
        const usedQty = parseFloat(item.used_qty) || 0;
        const mainIndentQty =
          item.main_indent_qty !== undefined
            ? parseFloat(item.main_indent_qty)
            : indentQty - usedQty;

        return {
          category: item.category || "",
          category_name: item.category_name || item.category?.name || "",
          sub_category: item.sub_category || "",
          sub_category_name:
            item.sub_category_name || item.sub_category?.name || "",
          item: item.item || "",
          item_name: item.item_name || item.item?.name || "",
          description: item.item_description || item.item_name || "",
          indent_qty: indentQty,
          used_qty: usedQty,
          main_indent_qty: mainIndentQty,
          quantity: 0,
          unit: item.uom || "",
          unit_name: item.uom_name || item.uom || "",
          size: sizeId,
          size_name: sizeName,
          rate: parseFloat(item.estimated_rate) || 0,
          amount: 0,
        };
      });

      setFormData((prev) => ({
        ...prev,
        indent: indentId,
        project: selectedIndent.project || "",
        division: selectedIndent.division || prev.division,
        items: processedItems,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        indent: "",
        project: "",
        items: [createEmptyItem()],
      }));
    }
  };

  const createEmptyItem = () => ({
    category: "",
    category_name: "",
    sub_category: "",
    sub_category_name: "",
    item: "",
    description: "",
    indent_qty: 0,
    quantity: 0,
    used_qty: 0,
    main_indent_qty: 0,
    unit: "",
    unit_name: "",
    size: "",
    size_name: "",
    rate: "",
    amount: "",
  });

  return (
    <Box sx={{ px: 0, pt: 0, mt: -3, maxWidth: 1200, mx: "auto" }}>
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar />

      {/* ✅ BUTTONS */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mb: 2 }}>
        <Button
          type="button"
          variant="outlined"
          color="secondary"
          onClick={handleSaveDraft}
          sx={{
            borderRadius: 0,
            height: 45,
            fontSize: "0.8rem",
            color: "#ff8400ff",
            borderColor: "#ff8400ff",
            minWidth: isMobile ? "auto" : 120,
            px: isMobile ? 2 : 3,
          }}
        >
          Draft
        </Button>
        <Button
          type="submit"
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          sx={{
            height: 45,
            fontSize: "0.8rem",
            borderRadius: 0,
            minWidth: isMobile ? "auto" : 200,
            px: isMobile ? 2 : 3,
          }}
        >
          Submit Purchase Order
        </Button>
      </Box>

      {/* Main Fields Card */}
      <Box
        border="1px solid #ccc"
        borderRadius="8px"
        sx={{ mb: 3, p: 2 }}
        autoComplete="off"
      >
        <Grid container spacing={2} sx={{ mb: 1 }}>
          <Grid item xs={12} sm={3} md={3}>
            <TextField
              fullWidth
              label="PO Number"
              required
              value={formData.po_number || ""}
              onChange={handleChange("po_number")}
              error={!!errors.po_number}
              helperText={errors.po_number}
              disabled
              InputProps={{
                sx: {
                  height: 45,
                  fontSize: "0.875rem",
                },
              }}
            />
          </Grid>

          {/* ✅ Division - Read-only from localStorage */}
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Division"
              required
              value={
                formData.division_name ||
                storedDivision?.division ||
                "No Division Selected"
              }
              disabled
              InputProps={{
                sx: {
                  height: 45,
                  fontSize: "0.875rem",
                  backgroundColor: "#f5f5f5", // ✅ Visual indication it's read-only
                },
              }}
              helperText="Division is set globally"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              options={indents}
              required
              getOptionLabel={(option) =>
                option.indent_no || `Indent ${option.indent_id}`
              }
              loading={indentLoading}
              isOptionEqualToValue={(option, value) =>
                option.indent_id === value?.indent_id
              }
              value={
                indents.find((i) => i.indent_id === formData.indent) || null
              }
              onChange={(e, newValue) => {
                if (newValue) {
                  handleIndentChange(newValue.indent_id);
                } else {
                  handleIndentChange("");
                }
              }}
              onInputChange={(e, newInputValue) => {
                handleIndentSearchChange(newInputValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Indent"
                  fullWidth
                  required
                  InputProps={{
                    ...params.InputProps,
                    sx: {
                      height: 45,
                      fontSize: "0.875rem",
                    },
                  }}
                  error={!!errors.indent}
                  helperText={errors.indent}
                />
              )}
              disabled={!formData.division}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              options={projects}
              required
              getOptionLabel={(option) =>
                option.project_name || `Project ${option.project_name}`
              }
              value={projects.find((i) => i.id === formData.project) || null}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              onChange={(e, newValue) => {
                if (newValue) {
                  setFormData((prev) => ({
                    ...prev,
                    project: newValue.id,
                    division: newValue.division || "",
                  }));
                } else {
                  setFormData((prev) => ({
                    ...prev,
                    project: "",
                    division: "",
                  }));
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Project"
                  fullWidth
                  required
                  InputProps={{
                    ...params.InputProps,
                    sx: {
                      height: 45,
                      fontSize: "0.875rem",
                    },
                  }}
                  error={!!errors.project}
                  helperText={errors.project}
                />
              )}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Billed To Card */}
      <Box border="1px solid #ccc" borderRadius="8px" sx={{ mb: 3, p: 2 }}>
        <Typography
          variant="h6"
          gutterBottom
          sx={{ mb: 2, fontWeight: 600, color: "#1976d2" }}
        >
          Billed To
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Company Name"
              value={billedTo.name}
              onChange={handleBilledToChange("name")}
              InputProps={{
                sx: {
                  height: 45,
                  fontSize: "0.875rem",
                },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Contact Details"
              value={billedTo.contact}
              onChange={handleBilledToChange("contact")}
              InputProps={{
                sx: {
                  height: 45,
                  fontSize: "0.875rem",
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={billedTo.email}
              onChange={handleBilledToChange("email")}
              InputProps={{
                sx: {
                  height: 45,
                  fontSize: "0.875rem",
                },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={1.5}>
            <TextField
              fullWidth
              label="State Name"
              value={billedTo.state_name}
              onChange={handleBilledToChange("state_name")}
              InputProps={{
                sx: {
                  height: 45,
                  fontSize: "0.875rem",
                },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={1.5}>
            <TextField
              fullWidth
              label="State Code"
              value={billedTo.state_code}
              onChange={handleBilledToChange("state_code")}
              InputProps={{
                sx: {
                  height: 45,
                  fontSize: "0.875rem",
                },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="GST No"
              value={billedTo.gst_no}
              onChange={handleBilledToChange("gst_no")}
              InputProps={{
                sx: {
                  height: 45,
                  fontSize: "0.875rem",
                },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="PAN No"
              value={billedTo.pan_no}
              onChange={handleBilledToChange("pan_no")}
              InputProps={{
                sx: {
                  height: 45,
                  fontSize: "0.875rem",
                },
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Address"
              value={billedTo.address}
              onChange={handleBilledToChange("address")}
              InputProps={{
                sx: {
                  height: 45,
                  fontSize: "0.875rem",
                },
              }}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Billed From Card (Supplier Details) */}
      <Box border="1px solid #ccc" borderRadius="8px" sx={{ mb: 3, p: 2 }}>
        <Typography
          variant="h6"
          gutterBottom
          sx={{ mb: 2, fontWeight: 600, color: "#1976d2" }}
        >
          Billed From
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <Autocomplete
              options={suppliers}
              required
              getOptionLabel={(option) => option.name || ""}
              value={suppliers.find((s) => s.id === formData.supplier) || null}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              onChange={(e, newValue) => {
                if (newValue) {
                  setFormData((prev) => ({
                    ...prev,
                    supplier: newValue.id,
                    billing_name: newValue.name || "",
                    billing_address: newValue.address || "",
                    billing_pan: newValue.pan || "",
                    billing_gst: newValue.gst || "",
                    billing_email: newValue.email || "",
                    billing_contact: newValue.contact || "",
                  }));
                } else {
                  setFormData((prev) => ({
                    ...prev,
                    supplier: "",
                    billing_name: "",
                    billing_address: "",
                    billing_pan: "",
                    billing_gst: "",
                    billing_email: "",
                    billing_contact: "",
                  }));
                }
              }}
              onInputChange={(e, newInputValue) => {
                setSupplierSearch(newInputValue);
                debouncedFetchSuppliers(newInputValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Supplier"
                  fullWidth
                  required
                  InputProps={{
                    ...params.InputProps,
                    sx: {
                      height: 45,
                      fontSize: "0.875rem",
                    },
                  }}
                  error={!!errors.supplier}
                  helperText={errors.supplier}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Supplier Name"
              required
              value={formData.billing_name || ""}
              onChange={handleChange("billing_name")}
              error={!!errors.billing_name}
              helperText={errors.billing_name}
              InputProps={{
                sx: {
                  height: 45,
                  fontSize: "0.875rem",
                },
              }}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={formData.billing_email || ""}
              onChange={handleChange("billing_email")}
              InputProps={{
                sx: {
                  height: 45,
                  fontSize: "0.875rem",
                },
              }}
            />
          </Grid>

          {/* ✅ Add Contact Field */}
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Contact"
              value={formData.billing_contact || ""}
              onChange={handleChange("billing_contact")}
              inputProps={{ maxLength: 10 }}
              InputProps={{
                sx: {
                  height: 45,
                  fontSize: "0.875rem",
                },
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Supplier Address"
              required
              value={formData.billing_address || ""}
              onChange={handleChange("billing_address")}
              error={!!errors.billing_address}
              helperText={errors.billing_address}
              InputProps={{
                sx: {
                  height: 45,
                  fontSize: "0.875rem",
                },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="PAN"
              value={formData.billing_pan || ""}
              onChange={(e) => {
                const value = e.target.value.toUpperCase();
                setFormData({ ...formData, billing_pan: value });
                validateField("billing_pan", value);
              }}
              error={!!errors.billing_pan}
              helperText={errors.billing_pan}
              InputProps={{
                sx: {
                  height: 45,
                  fontSize: "0.875rem",
                },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="GST"
              value={formData.billing_gst || ""}
              onChange={(e) => {
                const value = e.target.value.toUpperCase();
                setFormData({ ...formData, billing_gst: value });
                validateField("billing_gst", value);
              }}
              error={!!errors.billing_gst}
              helperText={errors.billing_gst}
              InputProps={{
                sx: {
                  height: 45,
                  fontSize: "0.875rem",
                },
              }}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Shipping Section */}
      <Box sx={{ mb: 3 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={includeShipping}
              onChange={(e) => setIncludeShipping(e.target.checked)}
            />
          }
          label="Include Shipping Details"
        />
        {includeShipping && (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Shipping Address"
                value={formData.shipping_address || ""}
                onChange={handleChange("shipping_address")}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Contact Person"
                value={formData.shipping_contact || ""}
                onChange={handleChange("shipping_contact")}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Phone"
                value={formData.shipping_phone || ""}
                onChange={handleChange("shipping_phone")}
                inputProps={{ maxLength: 10 }}
              />
            </Grid>
          </Grid>
        )}
      </Box>

      {/* TABLE SECTION */}
      {isMobile ? (
        // Mobile Card Layout
        <Box sx={{ mb: 3 }}>
          {(formData.items || []).map((item, index) => (
            <Card key={index} sx={{ mb: 2, border: "1px solid #ddd" }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Item #{index + 1}
                </Typography>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <TextField
                    fullWidth
                    label="Category"
                    value={item.category_name}
                    size="small"
                    disabled
                  />

                  <TextField
                    fullWidth
                    label="Sub Category"
                    value={item.sub_category_name}
                    size="small"
                    disabled
                  />

                  <TextField
                    fullWidth
                    label="Item"
                    value={item.item_name}
                    size="small"
                    disabled
                  />

                  <TextField
                    fullWidth
                    label="Description"
                    value={item.description}
                    onChange={(e) =>
                      handleItemChange(index, "description", e.target.value)
                    }
                    size="small"
                  />

                  <TextField
                    fullWidth
                    label="Size"
                    value={item.size_name}
                    size="small"
                    disabled
                  />

                  <TextField
                    fullWidth
                    label="Indent Qty"
                    type="number"
                    value={item.indent_qty}
                    onChange={(e) =>
                      handleItemChange(index, "indent_qty", e.target.value)
                    }
                    size="small"
                    disabled
                  />

                  <TextField
                    fullWidth
                    label="PO Qty"
                    type="number"
                    value={item.quantity}
                    onChange={(e) =>
                      handleItemChange(index, "quantity", e.target.value)
                    }
                    size="small"
                    error={!!errors[`items.${index}.quantity`]}
                    helperText={errors[`items.${index}.quantity`]}
                  />

                  <TextField
                    fullWidth
                    label="Used Qty"
                    type="number"
                    value={item.used_qty}
                    onChange={(e) =>
                      handleItemChange(index, "used_qty", e.target.value)
                    }
                    size="small"
                    disabled
                  />

                  <TextField
                    fullWidth
                    label="Unit"
                    value={item.unit_name}
                    size="small"
                    disabled
                  />

                  <TextField
                    fullWidth
                    label="Unit Price"
                    type="number"
                    value={item.rate}
                    onChange={(e) =>
                      handleItemChange(index, "rate", e.target.value)
                    }
                    size="small"
                    error={!!errors[`items.${index}.rate`]}
                    helperText={errors[`items.${index}.rate`]}
                  />

                  <TextField
                    fullWidth
                    label="Amount"
                    type="number"
                    value={item.amount}
                    onChange={(e) =>
                      handleItemChange(index, "amount", e.target.value)
                    }
                    size="small"
                  />

                  <TextField
                    fullWidth
                    label="Total"
                    value={(
                      parseFloat(item.rate || 0) *
                      parseFloat(item.quantity || 0)
                    ).toFixed(2)}
                    size="small"
                    disabled
                  />

                  <Button
                    fullWidth
                    variant="outlined"
                    color="error"
                    onClick={() =>
                      setFormData((prev) => ({
                        ...prev,
                        items: prev.items.filter((_, i) => i !== index),
                      }))
                    }
                    startIcon={<DeleteIcon />}
                  >
                    Delete Item
                  </Button>
                </Box>
              </CardContent>
            </Card>
          ))}

          <Typography variant="h6" align="right" sx={{ mt: 2 }}>
            Grand Total: ₹ {totalValue.toFixed(2)}
          </Typography>
        </Box>
      ) : (
        // Desktop Table
        <TableContainer sx={{ mt: 2 }}>
          <Box
            border="1px solid #ccc"
            borderRadius="8px"
            sx={{ mb: 3, p: 2, minWidth: 1800 }}
          >
            <Table>
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor: "#f5f5f5",
                    "& th": {
                      fontWeight: "bold",
                      color: "#333",
                      borderBottom: "1px solid #999",
                      backgroundColor: "#f5f5f5",
                    },
                  }}
                >
                  <TableCell>S.No</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Sub Category</TableCell>
                  <TableCell>Item</TableCell>
                  <TableCell>Size</TableCell>
                  <TableCell>UOM</TableCell>
                  <TableCell>Indent Qty</TableCell>
                  <TableCell>PO Qty</TableCell>
                  <TableCell>Used Qty</TableCell>
                  <TableCell>Unit Price</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Total</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Action</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {(formData.items || []).map((item, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      backgroundColor: "#fff !important",
                      "&:hover": {
                        backgroundColor: "#fff !important",
                      },
                    }}
                  >
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>
                      <TextField
                        value={item.category_name}
                        size="small"
                        sx={{
                          minWidth: "50px",
                          backgroundColor: "#ffffffff",
                          "& .MuiInputBase-root": {
                            height: "32px",
                          },
                        }}
                        disabled
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        value={item.sub_category_name}
                        size="small"
                        sx={{
                          minWidth: "50px",
                          backgroundColor: "#ffffff",
                          "& .MuiInputBase-root": {
                            height: "32px",
                          },
                        }}
                        disabled
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        value={item.item_name}
                        size="small"
                        sx={{
                          minWidth: "50px",
                          backgroundColor: "#ffffff",
                          "& .MuiInputBase-root": {
                            height: "32px",
                          },
                        }}
                        disabled
                      />
                    </TableCell>

                    <TableCell>
                      <TextField
                        value={item.size_name}
                        size="small"
                        sx={{
                          minWidth: "50px",
                          backgroundColor: "#ffffff",
                          "& .MuiInputBase-root": {
                            height: "32px",
                          },
                        }}
                        disabled
                      />
                    </TableCell>

                    <TableCell>
                      <TextField
                        value={item.unit_name}
                        size="small"
                        sx={{
                          minWidth: "50px",
                          backgroundColor: "#ffffff",
                          "& .MuiInputBase-root": {
                            height: "32px",
                          },
                        }}
                        disabled
                      />
                    </TableCell>
                    <TableCell>
                      <input
                        type="number"
                        value={item.indent_qty}
                        style={inputStyle}
                        disabled
                      />
                    </TableCell>
                    <TableCell>
                      <input
                        type="number"
                        value={item.quantity}
                        onChange={(e) =>
                          handleItemChange(index, "quantity", e.target.value)
                        }
                        style={{
                          ...inputStyle,
                          border: errors[`items.${index}.quantity`]
                            ? "1px solid red"
                            : inputStyle.border,
                        }}
                      />
                      {errors[`items.${index}.quantity`] && (
                        <div
                          style={{
                            color: "red",
                            fontSize: "0.8rem",
                            marginTop: "4px",
                          }}
                        >
                          {errors[`items.${index}.quantity`]}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <input
                        type="number"
                        style={inputStyle}
                        value={item.used_qty}
                        disabled
                      />
                    </TableCell>

                    <TableCell>
                      <input
                        type="number"
                        value={item.rate}
                        onChange={(e) =>
                          handleItemChange(index, "rate", e.target.value)
                        }
                        style={{
                          ...inputStyle,
                          border: errors[`items.${index}.rate`]
                            ? "1px solid red"
                            : inputStyle.border,
                        }}
                      />
                      {errors[`items.${index}.rate`] && (
                        <div
                          style={{
                            color: "red",
                            fontSize: "0.8rem",
                            marginTop: "4px",
                          }}
                        >
                          {errors[`items.${index}.rate`]}
                        </div>
                      )}
                    </TableCell>

                    <TableCell>
                      <input
                        type="number"
                        value={item.amount}
                        onChange={(e) =>
                          handleItemChange(index, "amount", e.target.value)
                        }
                        style={inputStyle}
                      />
                    </TableCell>

                    <TableCell>
                      <input
                        type="text"
                        readOnly
                        value={(
                          parseFloat(item.rate || 0) *
                          parseFloat(item.quantity || 0)
                        ).toFixed(2)}
                        style={inputStyle}
                      />
                    </TableCell>

                    <TableCell>
                      <input
                        type="text"
                        value={item.description}
                        onChange={(e) =>
                          handleItemChange(index, "description", e.target.value)
                        }
                        style={inputStyle}
                      />
                    </TableCell>

                    <TableCell>
                      <IconButton
                        onClick={() =>
                          setFormData((prev) => ({
                            ...prev,
                            items: prev.items.filter((_, i) => i !== index),
                          }))
                        }
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </TableContainer>
      )}

      {/* Grand Total */}
      {!isMobile && (
        <TableContainer>
          <Box sx={{ mb: 3, width: "100%" }}>
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell align="right">
                    <strong>Grand Total</strong>
                  </TableCell>
                  <TableCell align="left">
                    <TextField
                      fullWidth
                      value={totalValue.toFixed(2)}
                      size="small"
                      sx={{ width: "150px" }}
                      disabled
                    />
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableBody>
            </Table>
          </Box>
        </TableContainer>
      )}

      {/* Terms & Conditions */}
      <Box
        border="1px solid #ccc"
        borderRadius="8px"
        sx={{ mb: 3, p: isMobile ? 1.5 : 2 }}
      >
        <Typography variant="h6" gutterBottom>
          Terms & Conditions
        </Typography>

        {terms.map((term, index) => (
          <Grid
            container
            spacing={1}
            key={index}
            alignItems="center"
            sx={{ mb: 1 }}
          >
            <Grid item xs={5}>
              <TextField
                fullWidth
                placeholder="Term"
                value={term.key}
                onChange={(e) => handleTermChange(index, "key", e.target.value)}
                size="small"
                sx={{
                  "& .MuiInputBase-input": {
                    fontSize: isMobile ? "0.8rem" : "0.875rem",
                  },
                }}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                placeholder="Value"
                value={term.value}
                onChange={(e) =>
                  handleTermChange(index, "value", e.target.value)
                }
                size="small"
                sx={{
                  "& .MuiInputBase-input": {
                    fontSize: isMobile ? "0.8rem" : "0.875rem",
                  },
                }}
              />
            </Grid>

            <Grid
              item
              xs={1}
              sx={{
                display: "flex",
                justifyContent: "center",
              }}
            >
              <IconButton
                onClick={() => removeTerm(index)}
                color="error"
                size={isMobile ? "small" : "medium"}
              >
                <DeleteIcon fontSize={isMobile ? "small" : "medium"} />
              </IconButton>
            </Grid>
          </Grid>
        ))}

        <Dialog
          open={openItemModal}
          onClose={handleCloseItemModal}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle>Add Item</DialogTitle>
          <DialogContent>
            <AddItemForm onClose={handleCloseItemModal} />
          </DialogContent>
        </Dialog>

        <Button
          variant="outlined"
          sx={{ mt: 1 }}
          onClick={() =>
            setTerms([...terms, { key: "", value: "", is_default: false }])
          }
        >
          + Add Term
        </Button>
      </Box>
    </Box>
  );
}
